/**
 * 사용 설명서 업데이트 내역 — 데이터 + 순수부.
 *
 * ⚠ import를 넣지 말 것. scripts/test-help-changelog.js가 이 파일을 transpileModule로 통째로 평가해
 * node에서 검증한다(helpSearch.ts와 같은 관례). 화면은 sections/Changelog.tsx(15장)와 ui.tsx의 Sub 칩이다.
 *
 * 기록 규칙 (CLAUDE.md '사용설명서 업데이트 내역'):
 *  · 사용자가 체감하는 기능 추가·변경·수정은 맨 위 항목에 한 줄을 남긴다. 최신이 맨 위다
 *  · date = 처음 배포한 날(KST), version = 그날 마지막으로 발행한 PC 앱 버전. 서버·웹만 바뀐 날은 version 없음.
 *    같은 날 PC 앱을 두 번 발행했으면 버전별로 나눠 버전 내림차순으로 둔다
 *  · section = HELP_SECTIONS의 장 id, anchor = 그 장 본문 `<Sub anchor="…">`(이름은 `{장 id}-{영문}`).
 *    anchor가 있으면 내역에서 그 소제목으로 이동하고, 최근 RECENT_BADGE_DAYS일 안의 new/changed는
 *    소제목 옆에 칩이 붙는다(fixed는 설명이 바뀌지 않는 경우가 대부분이라 칩을 달지 않는다)
 *  · 문구는 사용자 말로, 마침표 없이. 관리자 콘솔 전용 변경은 적지 않는다
 *  · ⚠ 대화 열람·감사에 관한 변경은 적지 않는다 — 그 안내는 채팅 이용 안내 창 한 곳에만 둔다(2026-11-04 사용자 결정)
 */

export type HelpChangeKind = 'new' | 'changed' | 'fixed';

export interface HelpChange {
  kind: HelpChangeKind;
  text: string;
  /** HELP_SECTIONS의 장 id */
  section: string;
  /** 그 장 본문의 `<Sub anchor="…">` — 있으면 내역에서 그 소제목으로 이동하고 칩이 붙는다 */
  anchor?: string;
}

export interface HelpRelease {
  /** 배포일(KST) 'YYYY-MM-DD' */
  date: string;
  /** 그날 발행한 PC 앱 버전 — 서버·웹만 바뀐 날은 없다 */
  version?: string;
  changes: HelpChange[];
}

export const HELP_CHANGELOG: HelpRelease[] = [
  {
    date: '2026-11-04',
    version: '2.1.5',
    changes: [
      {
        kind: 'new',
        text: '통화 채팅 — 게스트를 포함한 통화 참가자끼리만 보는 채팅, 통화가 끝나면 사라짐',
        section: 'calls',
        anchor: 'calls-chat',
      },
      {
        kind: 'new',
        text: '사용 설명서 업데이트 내역 — 버전별로 새로 생긴 기능과 바뀐 점, 최근 바뀐 설명에는 소제목 옆에 표시',
        section: 'changelog',
      },
    ],
  },
  {
    date: '2026-11-04',
    version: '2.1.4',
    changes: [
      {
        kind: 'new',
        text: '통화 중에 초대하기 — 대화방 멤버를 다시 부르거나 대화방 밖 동료를 게스트로 초대',
        section: 'calls',
        anchor: 'calls-invite',
      },
      {
        kind: 'fixed',
        text: '다른 대화방을 보고 있던 멤버에게 그룹 통화 벨이 울리지 않던 문제',
        section: 'calls',
        anchor: 'calls-receive',
      },
    ],
  },
  {
    date: '2026-11-03',
    version: '2.1.3',
    changes: [
      {
        kind: 'new',
        text: '여러 명 화상통화 — 그룹 대화방에서 최대 6명, 늦게 와도 대화방 위 참여 버튼으로 들어가기',
        section: 'calls',
        anchor: 'calls-group',
      },
      {
        kind: 'new',
        text: '카메라·마이크가 없거나 다른 프로그램이 쓰는 중이어도 통화 참여',
        section: 'calls',
        anchor: 'calls-no-device',
      },
      {
        kind: 'fixed',
        text: '통화가 연결돼도 영상과 소리가 오가지 않던 문제',
        section: 'calls',
      },
      {
        kind: 'changed',
        text: '이름을 정하지 않은 그룹 대화방은 사람마다 자기를 뺀 참여자 이름으로 표시',
        section: 'rooms',
        anchor: 'rooms-group',
      },
    ],
  },
  {
    date: '2026-11-03',
    version: '2.1.1',
    changes: [
      {
        kind: 'changed',
        text: '인트라넷 비밀번호를 바꾸면 로그인해 둔 모든 기기에서 다시 로그인',
        section: 'security',
        anchor: 'security-account',
      },
    ],
  },
  {
    date: '2026-10-30',
    version: '1.1.194',
    changes: [
      {
        kind: 'new',
        text: "설정의 '내 기기' — 로그인한 기기 확인, 새 휴대폰 본인 확인, 잃어버린 기기 해제",
        section: 'security',
        anchor: 'security-devices',
      },
      {
        kind: 'changed',
        text: 'PC 앱 설치 파일은 인트라넷 자료실에서 받고, 회사 네트워크에서만 새로 설치',
        section: 'start',
        anchor: 'start-install',
      },
      {
        kind: 'fixed',
        text: '서버 점검이나 네트워크 오류 때 PC 앱 자동 로그인 정보가 지워지던 문제',
        section: 'start',
        anchor: 'start-login',
      },
    ],
  },
  {
    date: '2026-10-23',
    changes: [
      {
        kind: 'changed',
        text: 'PC에서 알림 탭을 열면 오른쪽 대화창이 닫히고, 알림을 누르면 그 대화가 오른쪽에 열림',
        section: 'notifications',
        anchor: 'notifications-activity',
      },
      {
        kind: 'changed',
        text: '관리자가 지정한 회사 일정 담당자도 회사 일정을 등록·수정',
        section: 'calendar',
        anchor: 'calendar-create',
      },
    ],
  },
  {
    date: '2026-10-15',
    version: '1.1.190',
    changes: [
      {
        kind: 'changed',
        text: '원격지원 중 담당자가 조작할 때 마우스 움직임이 더 매끄럽게 보임',
        section: 'remote-support',
        anchor: 'remote-support-control',
      },
    ],
  },
  {
    date: '2026-10-14',
    changes: [
      {
        kind: 'new',
        text: '캘린더 — 내 일정·부서 일정·회사 일정, 반복·알림·참석자·대화방 공유',
        section: 'calendar',
      },
      {
        kind: 'new',
        text: "알림함 '일정' 탭 — 일정 알림과 초대·시간 변경·취소 알림",
        section: 'notifications',
        anchor: 'notifications-calendar',
      },
    ],
  },
  {
    date: '2026-10-08',
    version: '1.1.186',
    changes: [
      {
        kind: 'new',
        text: '원격지원 — 담당자가 내 허락을 받아 PC 화면을 보고, 한 번 더 허락하면 대신 조작',
        section: 'remote-support',
      },
      {
        kind: 'new',
        text: "원격지원 요청 — 담당자 프로필의 '원격지원 요청'으로 내가 먼저 요청, 모니터가 여러 대면 모두 함께 보임",
        section: 'remote-support',
        anchor: 'remote-support-request',
      },
      {
        kind: 'new',
        text: '원격지원 중 파일 주고받기 — 파일마다 내가 허용한 것만',
        section: 'remote-support',
        anchor: 'remote-support-files',
      },
      {
        kind: 'new',
        text: 'PC에서 읽은 대화의 휴대폰 알림이 저절로 사라짐(안드로이드)',
        section: 'notifications',
        anchor: 'notifications-incoming',
      },
      {
        kind: 'new',
        text: '예약 전송에 사진·파일 첨부, 대화방의 예약 목록을 한 창에서 수정·취소',
        section: 'messaging',
        anchor: 'messaging-schedule',
      },
    ],
  },
  {
    date: '2026-10-07',
    changes: [
      {
        kind: 'new',
        text: "프로필 보기 — 사람을 누르면 사진·소속·메일 주소가 보이고 '1:1 대화'로 대화 시작",
        section: 'rooms',
        anchor: 'rooms-profile',
      },
      {
        kind: 'changed',
        text: 'PC 홈 화면이 넓게 펼쳐지고 확인할 것·최근 대화·일정·동료를 한눈에',
        section: 'start',
        anchor: 'start-layout',
      },
    ],
  },
  {
    date: '2026-10-06',
    changes: [
      {
        kind: 'new',
        text: 'PC 앱 창을 좁게 줄여 쓰기 — 목록이나 대화 한 칸만 보임',
        section: 'start',
        anchor: 'start-layout',
      },
      {
        kind: 'changed',
        text: '─ 버튼은 작업표시줄로 최소화, × 버튼은 트레이로 숨기기, 창 크기와 위치 기억',
        section: 'window',
        anchor: 'window-buttons',
      },
      {
        kind: 'changed',
        text: '대화방을 읽으면 그 방의 멘션·답장·반응 알림도 함께 읽음',
        section: 'notifications',
        anchor: 'notifications-activity',
      },
    ],
  },
  {
    date: '2026-10-02',
    changes: [
      {
        kind: 'fixed',
        text: '글과 표가 섞인 내용을 붙여넣으면 표만 남고 글이 사라지던 문제',
        section: 'messaging',
      },
      {
        kind: 'fixed',
        text: '휴대폰 알림으로 대화방을 연 뒤 뒤로가기를 누르면 앱이 꺼지던 문제(안드로이드)',
        section: 'web',
      },
      {
        kind: 'fixed',
        text: '내려받은 파일 이름 앞에 알 수 없는 글자가 붙던 문제',
        section: 'files',
      },
    ],
  },
];

/** 이 기간 안에 새로 생기거나 바뀐 소제목에 칩을 붙인다 */
export const RECENT_BADGE_DAYS = 30;

export const CHANGE_KIND_LABEL: Record<HelpChangeKind, string> = {
  new: '새 기능',
  changed: '변경',
  fixed: '수정',
};

const DAY_MS = 86400000;
const KST_OFFSET_MS = 9 * 3600000;
const pad2 = (n: number) => (n < 10 ? '0' : '') + n;

/** 시각(ms) → KST 날짜 키 'YYYY-MM-DD'. 기기 시간대를 믿지 않는다(캘린더와 같은 규칙) */
export function kstDateKey(ms: number): string {
  const d = new Date(ms + KST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** 날짜 키 → 1970-01-01부터 센 날 수. 형식이 틀리면 NaN */
export function dayNumber(key: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return NaN;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  // 2026-02-30처럼 없는 날짜는 다음 달로 넘어가므로 되읽어 거른다
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return NaN;
  return Math.round(ms / DAY_MS);
}

/** '2026-11-04' → '11.04' (칩용) */
export function shortDate(key: string): string {
  return key.slice(5).replace('-', '.');
}

/** 내역 장의 릴리스 제목: '2026-11-04 · PC 앱 v2.1.4' / 서버·웹만 바뀐 날은 날짜만 */
export function releaseTitle(release: HelpRelease): string {
  return release.version ? `${release.date} · PC 앱 v${release.version}` : release.date;
}

export interface AnchorBadge {
  kind: HelpChangeKind;
  date: string;
}

/**
 * 칩을 붙일 소제목 → { 종류, 날짜 }. 최근 `days`일(오늘 포함) 안의 new/changed만, 같은 소제목은 가장 최근 항목.
 * 내역은 최신이 위라 앞에서부터 처음 본 것을 쓴다. 오늘보다 뒤 날짜(배포 전에 미리 적은 항목)도 최근으로 본다.
 */
export function recentAnchorBadges(
  log: HelpRelease[],
  todayKey: string,
  days: number
): Record<string, AnchorBadge> {
  const today = dayNumber(todayKey);
  const out: Record<string, AnchorBadge> = {};
  log.forEach((release) => {
    const age = today - dayNumber(release.date);
    if (!(age < days)) return; // NaN도 여기서 걸러진다
    release.changes.forEach((change) => {
      if (!change.anchor || change.kind === 'fixed' || out[change.anchor]) return;
      out[change.anchor] = { kind: change.kind, date: release.date };
    });
  });
  return out;
}
